import { FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { Person, PersonType } from '@core/data-access/models/person.model';
import {
  TicketAssignmentStoreState,
  TicketHolder,
  TicketHolderForms,
  TicketHolderQuestionnaireForms
} from '@core/features/ticket-assignment/models/ticket-assignment-store.model';
import {
  disableAddressFormField,
  removeNonQuestionnaireFormFields,
  removeQuestionnaireFormFields,
  setDisabledFormFields
} from '@core/utils/form-fields/form-field-helpers';
import { isEmpty, isNil, merge, omitBy, pickBy, reduce } from 'lodash';

export const createTicketHolderBuyerPerson = (
  state: TicketAssignmentStoreState,
  createdPerson: Person,
  savedPerson: Person,
  formFields: FormFields
) => {
  if (isNil(createdPerson) || isNil(savedPerson) || isEmpty(formFields)) {
    return state;
  }

  const { ticketHolders, forms } = state;

  const createdBuyerIsHolderPerson: Person = { ...createdPerson, type: PersonType.BuyerIsHolder };

  const disabledFormFields = setDisabledFormFields(createdPerson, removeQuestionnaireFormFields(formFields));
  const mergedFormFields = merge({}, formFields, disabledFormFields);

  const newBuyerTicketHolder: TicketHolder = {
    person: createdPerson,
    formFields: mergedFormFields
  };

  if (isEmpty(forms[FormName.TicketHolder])) {
    return {
      ...state,
      ticketHolders: {
        ...ticketHolders,
        [newBuyerTicketHolder.person.uuid]: newBuyerTicketHolder
      }
    };
  }

  const ticketHoldersFormsWithPersonToReplace = pickBy(
    forms[FormName.TicketHolder],
    ({ person }) => person?.uuid === savedPerson.uuid
  );

  const updatedTicketHolderForms: TicketHolderForms = reduce(
    ticketHoldersFormsWithPersonToReplace,
    (updatedTicketHolderForms, _, tariffUuid) => {
      updatedTicketHolderForms[tariffUuid] = {
        formFields: mergedFormFields,
        person: createdBuyerIsHolderPerson
      };

      return updatedTicketHolderForms;
    },
    {}
  );

  if (isEmpty(forms[FormName.TicketHolderQuestionnaire])) {
    return {
      ...state,
      ticketHolders: {
        ...ticketHolders,
        [newBuyerTicketHolder.person.uuid]: newBuyerTicketHolder
      },
      forms: {
        ...forms,
        [FormName.TicketHolder]: {
          ...forms[FormName.TicketHolder],
          ...updatedTicketHolderForms
        }
      }
    };
  }

  const updatedTicketHolderQuestionnaireForms: TicketHolderQuestionnaireForms = reduce(
    forms[FormName.TicketHolderQuestionnaire],
    (updatedTicketHolderQuestionnaireForms, _, tariffUuid) => {
      updatedTicketHolderQuestionnaireForms[tariffUuid] = {
        formFields: removeNonQuestionnaireFormFields(mergedFormFields),
        person: createdBuyerIsHolderPerson
      };

      return updatedTicketHolderQuestionnaireForms;
    },
    {}
  );

  return {
    ...state,
    ticketHolders: {
      ...ticketHolders,
      [newBuyerTicketHolder.person.uuid]: newBuyerTicketHolder
    },
    forms: {
      ...forms,
      [FormName.TicketHolder]: {
        ...forms[FormName.TicketHolder],
        ...updatedTicketHolderForms
      },
      [FormName.TicketHolderQuestionnaire]: {
        ...forms[FormName.TicketHolderQuestionnaire],
        ...updatedTicketHolderQuestionnaireForms
      }
    }
  };
};

export const changeBuyerInformationPersonBackToSavedBuyerPerson = (
  state: TicketAssignmentStoreState,
  currentPerson: Person,
  savedPerson: Person,
  changedFormFields: FormFields
) => {
  if (isNil(currentPerson) || isNil(savedPerson) || isEmpty(changedFormFields)) {
    return state;
  }

  const { ticketHolders, forms } = state;

  const ticketHoldersWithoutCurrentPerson = pickBy(ticketHolders, ({ person }) => person.uuid !== currentPerson.uuid);

  if (isEmpty(forms[FormName.TicketHolder])) {
    return {
      ...state,
      ticketHolders: ticketHoldersWithoutCurrentPerson
    };
  }

  const ticketHoldersFormsWithPersonToReplace = pickBy(
    forms[FormName.TicketHolder],
    ({ person }) => person?.uuid === currentPerson.uuid
  );

  const savedPersonBuyerIsHolder: Person = { ...savedPerson, type: PersonType.BuyerIsHolder };

  const updatedTicketHolderForms: TicketHolderForms = reduce(
    ticketHoldersFormsWithPersonToReplace,
    (updatedTicketHolderForms, { formFields }, tariffUuid) => {
      const mergedFormFields = merge({}, formFields, changedFormFields);
      const removedQuestionnaireFormFields = removeQuestionnaireFormFields(mergedFormFields);
      const mergedAndDisabledFormFields = setDisabledFormFields(
        savedPersonBuyerIsHolder,
        removedQuestionnaireFormFields
      );
      const disabledFormFields = disableAddressFormField(mergedAndDisabledFormFields);

      updatedTicketHolderForms[tariffUuid] = {
        formFields: disabledFormFields,
        person: savedPersonBuyerIsHolder
      };

      return updatedTicketHolderForms;
    },
    {}
  );

  if (isEmpty(forms[FormName.TicketHolderQuestionnaire])) {
    return {
      ...state,
      ticketHolders: ticketHoldersWithoutCurrentPerson,
      forms: {
        ...forms,
        [FormName.TicketHolder]: {
          ...forms[FormName.TicketHolder],
          ...updatedTicketHolderForms
        }
      }
    };
  }

  const updatedTicketHolderQuestionnaireForms: TicketHolderQuestionnaireForms = reduce(
    forms[FormName.TicketHolderQuestionnaire],
    (updatedTicketHolderQuestionnaireForms, { formFields }, tariffUuid) => {
      updatedTicketHolderQuestionnaireForms[tariffUuid] = {
        formFields: merge({}, formFields, removeNonQuestionnaireFormFields(changedFormFields)),
        person: savedPersonBuyerIsHolder
      };

      return updatedTicketHolderQuestionnaireForms;
    },
    {}
  );

  return {
    ...state,
    ticketHolders: ticketHoldersWithoutCurrentPerson,
    forms: {
      ...forms,
      [FormName.TicketHolder]: {
        ...forms[FormName.TicketHolder],
        ...updatedTicketHolderForms
      },
      [FormName.TicketHolderQuestionnaire]: {
        ...forms[FormName.TicketHolderQuestionnaire],
        ...updatedTicketHolderQuestionnaireForms
      }
    }
  };
};

export const updateBuyerTicketHolderToSavedBuyerTicketHolder = (
  state: TicketAssignmentStoreState,
  person: Person,
  formFields: FormFields
) => {
  if (isNil(person)) {
    return state;
  }

  const { ticketHolders, forms } = state;

  const updatedTicketHoldersWithoutBuyer = omitBy(
    ticketHolders,
    ({ person: ticketHolder }) => ticketHolder.type === PersonType.Buyer
  );

  if (isEmpty(forms[FormName.TicketHolder])) {
    return {
      ...state,
      ticketHolders: updatedTicketHoldersWithoutBuyer
    };
  }

  const buyerTicketHolderForms = pickBy(
    forms[FormName.TicketHolder],
    ({ person }) => person?.type === PersonType.BuyerIsHolder
  );

  const updatedTicketHolderForms: TicketHolderForms = reduce(
    buyerTicketHolderForms,
    (updatedTicketHolderForms, _, tariffUuid) => {
      updatedTicketHolderForms[tariffUuid] = {
        person: {
          ...person,
          type: PersonType.BuyerIsHolder
        },
        formFields: removeQuestionnaireFormFields(formFields)
      };

      return updatedTicketHolderForms;
    },
    {}
  );

  if (isEmpty(forms[FormName.TicketHolderQuestionnaire])) {
    return {
      ...state,
      ticketHolders: updatedTicketHoldersWithoutBuyer,
      forms: {
        ...forms,
        [FormName.TicketHolder]: {
          ...forms[FormName.TicketHolder],
          ...updatedTicketHolderForms
        }
      }
    };
  }

  const updatedTicketHolderQuestionnaireForms: TicketHolderQuestionnaireForms = reduce(
    forms[FormName.TicketHolderQuestionnaire],
    (updatedTicketHolderQuestionnaireForms, _, tariffUuid) => {
      updatedTicketHolderQuestionnaireForms[tariffUuid] = {
        person: {
          ...person,
          type: PersonType.BuyerIsHolder
        },
        formFields: removeNonQuestionnaireFormFields(formFields)
      };

      return updatedTicketHolderQuestionnaireForms;
    },
    {}
  );

  return {
    ...state,
    ticketHolders: updatedTicketHoldersWithoutBuyer,
    forms: {
      ...forms,
      [FormName.TicketHolder]: {
        ...forms[FormName.TicketHolder],
        ...updatedTicketHolderForms
      },
      [FormName.TicketHolderQuestionnaire]: {
        ...forms[FormName.TicketHolderQuestionnaire],
        ...updatedTicketHolderQuestionnaireForms
      }
    }
  };
};
